import { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';
import { FileText, CheckCircle, Loader, AlertCircle, Download, Edit3 } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { useTheme } from './ThemeContext';

export default function PublicQuoteView({ quoteId }) {
  const { colors } = useTheme();
  console.log('PublicQuoteView RENDER, quoteId:', quoteId);

  const [quote, setQuote] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [depositPaid, setDepositPaid] = useState(false);
  const [payingDeposit, setPayingDeposit] = useState(false);
  const [depositJustPaid, setDepositJustPaid] = useState(false);
  const sigCanvas = useRef(null);

  useEffect(() => {
    console.log('PublicQuoteView useEffect running, quoteId:', quoteId);
    if (quoteId) {
      // Returning from the Stripe deposit checkout success_url (?deposit_paid=1).
      // Show confirmation immediately; the webhook flips deposit_paid server-side,
      // so re-fetch shortly after to pick up the persisted state.
      const params = new URLSearchParams(window.location.search);
      if (params.get('deposit_paid') === '1') {
        setDepositJustPaid(true);
        setDepositPaid(true);
        setTimeout(() => { loadQuote().catch(() => {}); }, 4000);
      }
      console.log('Calling loadQuote...');
      loadQuote().then(() => {
        console.log('loadQuote completed');
      }).catch(err => {
        console.error('loadQuote FAILED:', err);
      });
    } else {
      setLoading(false);
      setError('No quote ID provided');
    }
  }, [quoteId]);

  const loadQuote = async () => {
    console.log('loadQuote() STARTING');
    try {
      setLoading(true);
      setError(null);

      // Use direct fetch instead of Supabase client to avoid auth session issues
      const SUPABASE_URL = 'https://wrgmicftdykgaygbnkqn.supabase.co';
      const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6IndyZ21pY2Z0ZHlrZ2F5Z2Jua3FuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjU2MDIzNTYsImV4cCI6MjA4MTE3ODM1Nn0.CpRdUWBAti0qGCLKIZCaydk4h4Nnifx6apBEwy0k4E8';

      console.log('loadQuote: Fetching quote via REST API...');
      const selectQuery = '*,customers(id,name,first_name,last_name,address,city,state,zip,phone,email),companies(id,name,logo_url,quote_show_logo,quote_company_name,quote_company_phone,quote_company_email,quote_company_address,stripe_connect_id,stripe_charges_enabled)';
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/quotes?id=eq.${quoteId}&select=${encodeURIComponent(selectQuery)}`,
        {
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('loadQuote: Response status', response.status);
      const data = await response.json();
      console.log('loadQuote: Got data', data);

      if (!response.ok) {
        throw new Error(data.message || 'Failed to load quote');
      }

      const quoteData = data[0];
      const quoteError = quoteData ? null : { message: 'Quote not found' };

      console.log('loadQuote: Query COMPLETE', { hasData: !!quoteData, error: quoteError });

      if (quoteError) {
        console.error('Quote load error:', quoteError);
        throw new Error('Quote not found or not available for viewing');
      }

      if (!quoteData) {
        throw new Error('Quote not found');
      }

      setQuote(quoteData);
      setCompany(quoteData.companies);

      // Check if already signed
      if (quoteData.status === 'signed' || quoteData.signature_data) {
        setSigned(true);
      }

      // Reflect deposit-paid state (webhook flips this after online payment)
      if (quoteData.deposit_paid) {
        setDepositPaid(true);
      }

      // Pre-fill signer name from customer
      if (quoteData.customers) {
        const customer = quoteData.customers;
        setSignerName(customer.name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim());
      }

      // Load quote items separately using direct fetch
      console.log('loadQuote: Fetching quote items...');
      const itemsResponse = await fetch(
        `${SUPABASE_URL}/rest/v1/quote_items?quote_id=eq.${quoteId}&select=*`,
        {
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (itemsResponse.ok) {
        const items = await itemsResponse.json();
        console.log('loadQuote: Got items', items?.length || 0);
        if (items && items.length > 0) {
          setQuote(prev => ({ ...prev, items }));
        }
      } else {
        console.error('Quote items load error:', await itemsResponse.text());
      }
    } catch (err) {
      console.error('loadQuote CATCH block:', err);
      setError(err.message || 'Failed to load quote');
    } finally {
      console.log('loadQuote FINALLY - setting loading to false');
      setLoading(false);
    }
  };

  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setSignatureData(null);
    }
  };

  const handleSign = async () => {
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      alert('Please sign the quote first');
      return;
    }

    if (!signerName.trim()) {
      alert('Please enter your name');
      return;
    }

    try {
      setSigning(true);
      console.log('handleSign: Starting signature process...');

      // Supabase config for direct fetch
      const SUPABASE_URL = 'https://wrgmicftdykgaygbnkqn.supabase.co';
      const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6IndyZ21pY2Z0ZHlrZ2F5Z2Jua3FuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjU2MDIzNTYsImV4cCI6MjA4MTE3ODM1Nn0.CpRdUWBAti0qGCLKIZCaydk4h4Nnifx6apBEwy0k4E8';

      // Get signature as data URL
      const signatureDataUrl = sigCanvas.current.toDataURL('image/png');
      console.log('handleSign: Got signature data URL');

      // Upload signature to storage using direct fetch
      const signatureBlob = await (await fetch(signatureDataUrl)).blob();
      const signatureFileName = `${quoteId}-${Date.now()}.png`;

      let signatureUrl = null;
      try {
        console.log('handleSign: Uploading signature to storage...');
        const uploadResponse = await fetch(
          `${SUPABASE_URL}/storage/v1/object/quote-signatures/${signatureFileName}`,
          {
            method: 'POST',
            headers: {
              'apikey': SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
              'Content-Type': 'image/png',
              'x-upsert': 'true'
            },
            body: signatureBlob
          }
        );

        if (uploadResponse.ok) {
          signatureUrl = `${SUPABASE_URL}/storage/v1/object/public/quote-signatures/${signatureFileName}`;
          console.log('handleSign: Signature uploaded', signatureUrl);
        } else {
          console.warn('handleSign: Storage upload failed, continuing with data URL only');
        }
      } catch (uploadErr) {
        console.warn('handleSign: Storage upload error, continuing with data URL only', uploadErr);
      }

      // Update quote with signature using direct fetch
      console.log('handleSign: Updating quote status...');
      const updateResponse = await fetch(
        `${SUPABASE_URL}/rest/v1/quotes?id=eq.${quoteId}`,
        {
          method: 'PATCH',
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
            'Prefer': 'return=minimal'
          },
          body: JSON.stringify({
            status: 'signed',
            signature_data: signatureDataUrl,
            signature_url: signatureUrl,
            signer_name: signerName.trim(),
            signed_at: new Date().toISOString()
          })
        }
      );

      console.log('handleSign: Update response status', updateResponse.status);

      if (!updateResponse.ok) {
        const errorData = await updateResponse.json();
        throw new Error(errorData.message || 'Failed to save signature');
      }

      console.log('handleSign: Quote signed successfully!');
      setSigned(true);
      setSignatureData(signatureDataUrl);
    } catch (err) {

      alert('Failed to sign quote. Please try again.');
    } finally {
      setSigning(false);
    }
  };

  const handlePayDeposit = async () => {
    try {
      setPayingDeposit(true);
      const resp = await fetch('/api/deposit-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quoteId })
      });
      const data = await resp.json();
      if (!resp.ok || !data.url) {
        if (data.alreadyPaid) { setDepositPaid(true); return; }
        throw new Error(data.error || 'Could not start deposit payment');
      }
      window.location.href = data.url;
    } catch (err) {
      alert(err.message || 'Could not start deposit payment. Please try again.');
      setPayingDeposit(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5f5f5',
        fontFamily: 'Archivo, system-ui, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader size={48} style={{ animation: 'spin 1s linear infinite' }} color={colors.accent} />
          <p style={{ marginTop: '1rem', color: '#666' }}>Loading quote...</p>
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5f5f5',
        fontFamily: 'Archivo, system-ui, sans-serif',
        padding: '2rem'
      }}>
        <div style={{
          background: 'white',
          padding: '3rem',
          borderRadius: '12px',
          textAlign: 'center',
          maxWidth: '400px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}>
          <AlertCircle size={48} color="#dc2626" style={{ marginBottom: '1rem' }} />
          <h2 style={{ margin: '0 0 1rem', color: '#1a1a1a' }}>Quote Not Found</h2>
          <p style={{ color: '#666', margin: 0 }}>{error}</p>
        </div>
      </div>
    );
  }

  const items = quote?.items || [];
  const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  const discount = Number(quote?.discount) || 0;
  const afterDiscount = Math.max(0, subtotal - discount);
  const taxRate = quote?.tax_rate || 0;
  const taxAmount = afterDiscount * taxRate;
  const total = afterDiscount + taxAmount;
  // Deposit is a stored dollar amount on the quote (deposit_amount), gated by
  // deposit_required. (There is no deposit_percentage column.)
  const depositDue = quote?.deposit_required && Number(quote?.deposit_amount) > 0
    ? Number(quote.deposit_amount)
    : 0;
  const canCollectOnline = !!company?.stripe_charges_enabled && !!company?.stripe_connect_id;
  const balanceAfterDeposit = Math.max(0, total - depositDue);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f5f5',
      fontFamily: 'Archivo, system-ui, sans-serif',
      padding: '2rem 1rem',
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch'
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        {/* Header */}
        <div style={{
          background: '#1a1a1a',
          padding: '2rem',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              {company?.logo_url && company?.quote_show_logo !== false && (
                <img
                  src={company.logo_url}
                  alt={company.name}
                  style={{ height: '50px', borderRadius: '8px' }}
                />
              )}
              <div>
                <h1 style={{ margin: 0, fontSize: '1.5rem' }}>{company?.quote_company_name || company?.name}</h1>
                {company?.quote_company_phone && <p style={{ margin: '0.25rem 0 0', opacity: 0.8, fontSize: '0.9rem' }}>{company.quote_company_phone}</p>}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>Quote #{quote?.quote_number}</div>
              <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>{formatDate(quote?.created_at)}</div>
            </div>
          </div>
        </div>

        {/* Status Banner */}
        {signed && (
          <div style={{
            background: '#dcfce7',
            padding: '1rem 2rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            color: '#166534'
          }}>
            <CheckCircle size={24} />
            <div>
              <div style={{ fontWeight: '600' }}>Quote Signed</div>
              <div style={{ fontSize: '0.9rem' }}>
                Signed by {quote?.signer_name || signerName} on {formatDate(quote?.signed_at || new Date())}
              </div>
            </div>
          </div>
        )}

        {/* Quote Content */}
        <div style={{ padding: '2rem' }}>
          {/* Customer Info */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ margin: '0 0 0.5rem', color: '#666', fontSize: '0.85rem', textTransform: 'uppercase' }}>Prepared For</h3>
            <div style={{ color: '#1a1a1a' }}>
              <div style={{ fontWeight: '600', fontSize: '1.1rem' }}>
                {quote?.customers?.name || `${quote?.customers?.first_name || ''} ${quote?.customers?.last_name || ''}`.trim()}
              </div>
              {quote?.customers?.address && (
                <div style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.25rem' }}>
                  {quote.customers.address}
                  {quote.customers.city && `, ${quote.customers.city}`}
                  {quote.customers.state && `, ${quote.customers.state}`}
                  {quote.customers.zip && ` ${quote.customers.zip}`}
                </div>
              )}
            </div>
          </div>

          {/* Quote Title/Description */}
          {quote?.title && (
            <div style={{ marginBottom: '2rem' }}>
              <h2 style={{ margin: '0 0 0.5rem', color: '#1a1a1a' }}>{quote.title}</h2>
              {quote.description && <p style={{ margin: 0, color: '#666' }}>{quote.description}</p>}
            </div>
          )}

          {/* Line Items */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ margin: '0 0 1rem', color: '#666', fontSize: '0.85rem', textTransform: 'uppercase' }}>Items</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e5e5' }}>
                  <th style={{ textAlign: 'left', padding: '0.75rem 0', color: '#666', fontSize: '0.85rem' }}>Description</th>
                  <th style={{ textAlign: 'center', padding: '0.75rem', color: '#666', fontSize: '0.85rem' }}>Qty</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem', color: '#666', fontSize: '0.85rem' }}>Price</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem 0', color: '#666', fontSize: '0.85rem' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #e5e5e5' }}>
                    <td style={{ padding: '1rem 0' }}>
                      <div style={{ fontWeight: '500', color: '#1a1a1a' }}>{item.name}</div>
                      {item.description && (
                        <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>{item.description}</div>
                      )}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>{item.quantity}</td>
                    <td style={{ padding: '1rem', textAlign: 'right', color: '#666' }}>{formatCurrency(item.unit_price)}</td>
                    <td style={{ padding: '1rem 0', textAlign: 'right', fontWeight: '500', color: '#1a1a1a' }}>
                      {formatCurrency(item.quantity * item.unit_price)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div style={{
            background: '#f9f9f9',
            padding: '1.5rem',
            borderRadius: '8px',
            marginBottom: '2rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ color: '#666' }}>Subtotal</span>
              <span style={{ color: '#1a1a1a' }}>{formatCurrency(subtotal)}</span>
            </div>
            {discount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ color: '#666' }}>Discount</span>
                <span style={{ color: '#1a1a1a' }}>−{formatCurrency(discount)}</span>
              </div>
            )}
            {taxRate > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ color: '#666' }}>Tax ({+(taxRate * 100).toFixed(2)}%)</span>
                <span style={{ color: '#1a1a1a' }}>{formatCurrency(taxAmount)}</span>
              </div>
            )}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              paddingTop: '0.75rem',
              borderTop: '2px solid #e5e5e5',
              marginTop: '0.5rem'
            }}>
              <span style={{ fontWeight: '700', fontSize: '1.25rem', color: '#1a1a1a' }}>Total</span>
              <span style={{ fontWeight: '700', fontSize: '1.25rem', color: colors.accent }}>{formatCurrency(total)}</span>
            </div>
            {depositDue > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e5e5' }}>
                <span style={{ color: '#666' }}>
                  Deposit {depositPaid ? 'Paid' : 'Due'}
                </span>
                <span style={{ fontWeight: '600', color: depositPaid ? '#166534' : '#1a1a1a' }}>
                  {depositPaid && '✓ '}{formatCurrency(depositDue)}
                </span>
              </div>
            )}
            {depositDue > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.35rem' }}>
                <span style={{ color: '#999', fontSize: '0.85rem' }}>Balance {depositPaid ? 'Remaining' : 'After Deposit'}</span>
                <span style={{ color: '#999', fontSize: '0.85rem' }}>{formatCurrency(balanceAfterDeposit)}</span>
              </div>
            )}
          </div>

          {/* Deposit collection */}
          {depositDue > 0 && (
            depositPaid ? (
              <div style={{
                marginBottom: '2rem',
                padding: '1.25rem 1.5rem',
                background: '#dcfce7',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                color: '#166534'
              }}>
                <CheckCircle size={24} />
                <div>
                  <div style={{ fontWeight: '700' }}>Deposit Paid — {formatCurrency(depositDue)}</div>
                  <div style={{ fontSize: '0.9rem' }}>
                    {depositJustPaid
                      ? 'Thank you! Your payment is confirmed.'
                      : 'Your deposit is on file. Thank you!'}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{
                marginBottom: '2rem',
                padding: '1.5rem',
                background: '#fff7ed',
                border: '2px solid #f59e0b',
                borderRadius: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <AlertCircle size={20} color="#b45309" />
                  <h3 style={{ margin: 0, color: '#b45309', fontSize: '1rem' }}>
                    Deposit Required — {formatCurrency(depositDue)}
                  </h3>
                </div>
                <p style={{ margin: '0 0 1rem', color: '#92400e', fontSize: '0.9rem' }}>
                  A deposit of {formatCurrency(depositDue)} is required to secure your booking.
                  {canCollectOnline
                    ? ' Pay securely below.'
                    : ' The business will contact you to collect it.'}
                </p>
                {canCollectOnline && (
                  <button
                    onClick={handlePayDeposit}
                    disabled={payingDeposit}
                    style={{
                      width: '100%',
                      padding: '0.9rem',
                      background: payingDeposit ? '#ccc' : '#b45309',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '1.05rem',
                      fontWeight: '700',
                      cursor: payingDeposit ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    {payingDeposit ? (
                      <>
                        <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                        Redirecting to payment…
                      </>
                    ) : (
                      <>Pay Deposit {formatCurrency(depositDue)}</>
                    )}
                  </button>
                )}
              </div>
            )
          )}

          {/* Recurring Service Notice */}
          {quote?.is_recurring && (
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              background: '#e8f4fc',
              borderRadius: '8px',
              border: '1px solid #3498db'
            }}>
              <h3 style={{ margin: '0 0 0.5rem', color: '#2980b9', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.1rem' }}>🔄</span>
                Recurring Service Agreement
              </h3>
              <p style={{ margin: 0, color: '#2c3e50', fontSize: '0.9rem' }}>
                By signing this quote, you are agreeing to <strong style={{ color: '#2980b9' }}>
                  {quote.recurrence_rule === 'weekly' && 'weekly'}
                  {quote.recurrence_rule === 'biweekly' && 'bi-weekly (every 2 weeks)'}
                  {quote.recurrence_rule === 'monthly' && 'monthly'}
                  {quote.recurrence_rule === 'quarterly' && 'quarterly'}
                </strong> recurring service
                {quote.recurrence_end_date
                  ? ` until ${new Date(quote.recurrence_end_date).toLocaleDateString()}`
                  : ' (ongoing until cancelled)'
                }.
              </p>
              <p style={{ margin: '0.5rem 0 0', color: '#7f8c8d', fontSize: '0.8rem' }}>
                Each service visit will be billed at the rate shown above. You may cancel recurring service at any time with reasonable notice.
              </p>
            </div>
          )}

          {/* Terms & Conditions */}
          {quote?.terms && (
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ margin: '0 0 0.5rem', color: '#666', fontSize: '0.85rem', textTransform: 'uppercase' }}>Terms & Conditions</h3>
              <div style={{
                padding: '1rem',
                background: '#f9f9f9',
                borderRadius: '8px',
                fontSize: '0.85rem',
                color: '#666',
                whiteSpace: 'pre-wrap',
                maxHeight: '200px',
                overflow: 'auto'
              }}>
                {quote.terms}
              </div>
            </div>
          )}

          {/* Signature Section */}
          {!signed ? (
            <div style={{
              border: `2px solid ${colors.accent}`,
              borderRadius: '12px',
              padding: '1.5rem'
            }}>
              <h3 style={{ margin: '0 0 1rem', color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Edit3 size={20} color={colors.accent} />
                Sign to Accept Quote
              </h3>

              {/* Signer Name */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#1a1a1a' }}>
                  Your Name
                </label>
                <input
                  type="text"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Enter your full name"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '1rem',
                    border: '1px solid #e5e5e5',
                    borderRadius: '8px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Signature Pad */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#1a1a1a' }}>
                  Your Signature
                </label>
                <div style={{
                  border: '1px solid #e5e5e5',
                  borderRadius: '8px',
                  background: '#fff',
                  overflow: 'hidden'
                }}>
                  <SignatureCanvas
                    ref={sigCanvas}
                    canvasProps={{
                      style: {
                        width: '100%',
                        height: '150px',
                        cursor: 'crosshair'
                      }
                    }}
                    backgroundColor="white"
                  />
                </div>
                <button
                  onClick={clearSignature}
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem 1rem',
                    background: '#f5f5f5',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    color: '#666',
                    cursor: 'pointer'
                  }}
                >
                  Clear Signature
                </button>
              </div>

              {/* Agreement Text */}
              <p style={{
                margin: '0 0 1rem',
                fontSize: '0.85rem',
                color: '#666'
              }}>
                By signing above, I agree to the terms and conditions outlined in this quote and authorize the work described.
              </p>

              {/* Sign Button */}
              <button
                onClick={handleSign}
                disabled={signing}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: signing ? '#ccc' : colors.accent,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  cursor: signing ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {signing ? (
                  <>
                    <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                    Signing...
                  </>
                ) : (
                  <>
                    <CheckCircle size={20} />
                    Accept & Sign Quote
                  </>
                )}
              </button>
            </div>
          ) : (
            <div style={{
              background: '#dcfce7',
              padding: '1.5rem',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <CheckCircle size={48} color="#166534" style={{ marginBottom: '1rem' }} />
              <h3 style={{ margin: '0 0 0.5rem', color: '#166534' }}>Quote Accepted</h3>
              <p style={{ margin: 0, color: '#166534' }}>
                Thank you! We'll be in touch shortly to schedule your work.
              </p>
              {quote?.signature_data && (
                <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #bbf7d0' }}>
                  <p style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', color: '#166534' }}>Signature on file:</p>
                  <img
                    src={quote.signature_data}
                    alt="Signature"
                    style={{ maxWidth: '200px', background: 'white', padding: '0.5rem', borderRadius: '4px' }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '1.5rem 2rem',
          background: '#f9f9f9',
          borderTop: '1px solid #e5e5e5',
          textAlign: 'center',
          color: '#999',
          fontSize: '0.85rem'
        }}>
          Powered by Work Van
        </div>
      </div>

      <style>{`
        html, body, #root {
          height: 100%;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }
        body {
          margin: 0;
          touch-action: pan-y;
        }
      `}</style>
    </div>
  );
}
